import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'wouter';
import { analytics } from '@mindstudio-ai/interface';
import { useToasts } from '../state';
import { useNavOpen } from '../nav/NavSection';
import { useGuide, type GuideState, type GuideStep } from './store';
import './guide.css';

/** Any other modal on screen (capture dialog, announcements, confirm sheets). The guide never stacks on one. */
function otherDialogOpen() {
  return (
    !!document.querySelector('[aria-modal="true"]:not([data-guide-ui]), [aria-label="Cookie consent"]') ||
    document.documentElement.classList.contains('spark-modal-open')
  );
}

const MEMO_ROUTE = /^\/ideas\/([^/]+)\/(needs-discovery-memo|memos|business-case-memo)\/[^/]+/;
const WORK_ROUTE = /^\/ideas\/([^/]+)\/(needs-discovery|session|business-case|needs-discovery-memo|memos|business-case-memo)/;

const HIDDEN_TOAST = 'Guide hidden. You can bring it back from your avatar menu or My Learning.';

/** Step number the member is on (1–3), and how many are done. */
function progress(g: GuideState) {
  const done = (g.steps.idea ? 1 : 0) + (g.steps.strategist ? 1 : 0) + (g.steps.memo === 'opened' ? 1 : 0);
  return { done, current: Math.min(3, done + 1) };
}

/**
 * The first-run guide, mounted once in the app shell for signed-in members:
 * the welcome pop-up, the "click here next" spotlight, the first-memo card,
 * and the minimized "How to start" pill.
 */
export function GuideLayer({ isPhone, boundMain }: { isPhone: boolean; boundMain: boolean }) {
  const [location, navigate] = useLocation();
  const { guide, welcomeOpen, spotlight, memoCardOpen, load, act, set } = useGuide();

  // Refresh on every move between pages (steps complete through real work elsewhere in the app).
  useEffect(() => {
    const t = window.setTimeout(() => void load(), 300);
    return () => window.clearTimeout(t);
  }, [location, load]);
  useEffect(() => {
    const onFocus = () => void load();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [load]);
  // A memo being written finishes in the background: check back while it's writing.
  useEffect(() => {
    if (!guide?.active || guide.steps.memo !== 'writing') return;
    const t = window.setInterval(() => void load(), 20000);
    return () => window.clearInterval(t);
  }, [guide?.active, guide?.steps.memo, load]);

  // Welcome: first sign-in, on the Dashboard, once nothing else is in the way.
  useEffect(() => {
    if (!guide?.active || guide.welcomeSeen || welcomeOpen || location !== '/dashboard') return;
    let tries = 0;
    const t = window.setInterval(() => {
      tries++;
      if (otherDialogOpen() || !document.querySelector('main')) return;
      if (tries < 2) return; // ~500ms after the page has painted
      window.clearInterval(t);
      set({ welcomeOpen: true });
    }, 250);
    return () => window.clearInterval(t);
  }, [guide?.active, guide?.welcomeSeen, welcomeOpen, location, set]);

  // Step 3: opening a memo on one of their own ideas counts.
  const memoMatch = location.match(MEMO_ROUTE);
  const onOwnMemo = !!memoMatch && !!guide?.ideaIds.includes(memoMatch[1]);
  useEffect(() => {
    if (guide?.active && guide.mode === 'full' && onOwnMemo && guide.steps.memo === 'ready') {
      analytics.track('guide_step_completed', { step: 'memo' });
      void act('memoOpened');
    }
  }, [guide?.active, guide?.mode, guide?.steps.memo, onOwnMemo, act]);

  // First memo ready: announce it once, never over the advisor session or the memo itself.
  const workMatch = location.match(WORK_ROUTE);
  const onIdeaWork = !!workMatch;
  useEffect(() => {
    if (!guide?.active || guide.mode !== 'full' || guide.steps.memo !== 'ready' || onIdeaWork || welcomeOpen || spotlight) return;
    const key = 'ed.guide.memoAnnounced';
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, '1');
    } catch {
      /* private mode: show it anyway */
    }
    const t = window.setTimeout(() => {
      if (otherDialogOpen()) return;
      analytics.track('guide_memo_card_shown');
      set({ memoCardOpen: true });
    }, 600);
    return () => window.clearTimeout(t);
  }, [guide?.active, guide?.mode, guide?.steps.memo, onIdeaWork, welcomeOpen, spotlight, set]);

  // Things on the page that own the bottom corner (demo tours, docks, other dialogs).
  const [blocked, setBlocked] = useState(false);
  useEffect(() => {
    const check = () => {
      const root = document.documentElement;
      const keyboardUp = !!window.visualViewport && window.visualViewport.height < window.innerHeight * 0.75;
      setBlocked(
        !!document.querySelector('.sk-dg-float, .dw-demoguide, .dw-demoguide-pill') ||
          otherDialogOpen() ||
          (isPhone && (root.classList.contains('fbw-tucked') || !!document.querySelector('.msheet.is-open') || keyboardUp)),
      );
    };
    check();
    const t = window.setInterval(check, 700);
    return () => window.clearInterval(t);
  }, [isPhone, location]);

  const onDemo = location.startsWith('/demo') || location === '/design-sketch/demo' || location === '/pathway/demo';
  const showPill = !!guide?.active && guide.welcomeSeen && !welcomeOpen && !spotlight && !memoCardOpen && !boundMain && !onDemo && !blocked;

  useEffect(() => {
    document.documentElement.classList.toggle('has-guide', showPill);
    return () => document.documentElement.classList.remove('has-guide');
  }, [showPill]);

  if (!guide) return null;

  return createPortal(
    <>
      {welcomeOpen && (
        <Welcome
          isPhone={isPhone}
          hasIdea={guide.steps.idea}
          brief={guide.mode === 'brief'}
          onClose={(how) => {
            set({ welcomeOpen: false });
            void act('welcomeSeen', { brief: guide.mode === 'brief' });
            if (how === 'show' && guide.mode !== 'brief') {
              analytics.track('guide_welcome_finished');
              window.setTimeout(() => set({ spotlight: guide.next === 'memo' ? null : (guide.next ?? null) }), 220);
            }
          }}
        />
      )}
      {spotlight && spotlight !== 'memo' && (
        <Spotlight
          step={spotlight}
          isPhone={isPhone}
          stepNo={spotlight === 'idea' ? 1 : 2}
          onClose={(how) => {
            set({ spotlight: null });
            if (how === 'skip') {
              void act('hide');
              analytics.track('guide_hidden', { from: 'spotlight' });
              useToasts.getState().show(HIDDEN_TOAST);
            }
            if (how === 'missing') set({ cardOpen: true, cardAuto: false });
          }}
        />
      )}
      {memoCardOpen && guide.memoHref && (
        <MemoCard
          ideaTitle={guide.memoIdeaTitle}
          onOpen={() => {
            set({ memoCardOpen: false });
            navigate(guide.memoHref!);
          }}
          onLater={() => set({ memoCardOpen: false })}
        />
      )}
      {showPill && <HowToStart guide={guide} isPhone={isPhone} onMemoPage={onOwnMemo} />}
    </>,
    document.body,
  );
}

// ---------------------------------------------------------------- Welcome

const PATH = [
  { t: 'Capture your idea', s: 'A sentence or two is plenty.' },
  { t: 'Test it with your advisors', s: 'Market, IP and financial, one advisor each.' },
  { t: 'Design it', s: '2D sketches and a 3D model in your Design Workspace.' },
  { t: 'Protect it', s: 'Prior art, your filing path and deadlines. Start before you share it publicly.' },
  { t: 'Plan the business', s: 'Customer interviews, a business model canvas and progress tracking.' },
];

function Welcome({ isPhone, hasIdea, brief, onClose }: { isPhone: boolean; hasIdea: boolean; brief: boolean; onClose: (how: 'skip' | 'show') => void }) {
  // The one-screen version (members who already have a memo) is just the path screen.
  const [i, setI] = useState(brief ? 1 : 0);
  const [closing, setClosing] = useState(false);
  // Once the entry animation ends it's dropped, so a drag's inline transform can move the sheet.
  const [entered, setEntered] = useState(false);
  const nextRef = useRef<HTMLButtonElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    analytics.track('guide_welcome_shown', { mode: brief ? 'brief' : 'full' });
    nextRef.current?.focus();
  }, []);
  useEffect(() => {
    analytics.track('guide_welcome_screen', { n: i + 1 });
  }, [i]);

  const close = (how: 'skip' | 'show') => {
    if (closing) return;
    if (how === 'skip') analytics.track('guide_welcome_skipped', { screen: i + 1 });
    setClosing(true);
    window.setTimeout(() => onClose(how), isPhone ? 220 : 160);
  };
  const next = () => (brief || i === 2 ? close('show') : setI(i + 1));

  // Keys: Enter / arrows move, Esc skips. Focus stays inside.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); close('skip'); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); next(); }
      else if (e.key === 'ArrowLeft' && !brief) { e.preventDefault(); setI((v) => Math.max(0, v - 1)); }
      else if (e.key === 'Tab') {
        const els = sheetRef.current?.querySelectorAll<HTMLElement>('button:not([tabindex="-1"])');
        if (!els?.length) return;
        const first = els[0], last = els[els.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  // Phone: drag the sheet down to skip.
  const drag = useRef<{ y: number; t: number } | null>(null);
  const onDown = (e: React.PointerEvent) => {
    if (!isPhone) return;
    drag.current = { y: e.clientY, t: performance.now() };
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
  };
  const onMove = (e: React.PointerEvent) => {
    if (!drag.current || !sheetRef.current) return;
    const dy = e.clientY - drag.current.y;
    sheetRef.current.style.transition = 'none';
    sheetRef.current.style.transform = `translateY(${dy > 0 ? dy : dy * 0.2}px)`;
  };
  const onUp = (e: React.PointerEvent) => {
    if (!drag.current || !sheetRef.current) return;
    const dy = e.clientY - drag.current.y;
    const v = (dy / Math.max(1, performance.now() - drag.current.t)) * 1000;
    drag.current = null;
    const el = sheetRef.current;
    if (dy > el.offsetHeight * 0.3 || v > 600) {
      el.style.transition = 'transform 200ms cubic-bezier(0.4, 0, 1, 1)';
      el.style.transform = 'translateY(100%)';
      analytics.track('guide_welcome_skipped', { screen: i + 1 });
      window.setTimeout(() => onClose('skip'), 200);
    } else {
      el.style.transition = 'transform 260ms cubic-bezier(0.32, 0.72, 0, 1)';
      el.style.transform = '';
    }
  };

  const heads = ['Welcome. Let\u2019s see what you\u2019re working on.', brief ? 'How EntreDash fits together' : 'How it works', hasIdea ? 'Your idea is saved. Next, test it.' : 'Start with the idea itself.'];

  return (
    <>
      <div className={`gw-scrim${closing ? ' is-closing' : ''}`} data-guide-ui />
      <div
        ref={sheetRef}
        className={`gw${closing ? ' is-closing' : ''}${entered ? ' is-entered' : ''}`}
        onAnimationEnd={(e) => e.target === e.currentTarget && !closing && setEntered(true)}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`gw-h${i}`}
        data-guide-ui
      >
        <div className="gw__grab" onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}>
          <div className="gw__handle" />
          <div className="gw__top">
            <div className="gw__mark" aria-hidden="true">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2c.5 4.5 2.5 6.5 7 7-4.5.5-6.5 2.5-7 7-.5-4.5-2.5-6.5-7-7 4.5-.5 6.5-2.5 7-7Z" /></svg>
            </div>
            {!brief && (
              <>
                <div className="gw__dots" aria-hidden="true">
                  {[0, 1, 2].map((k) => <i key={k} className={k <= i ? 'on' : ''} />)}
                </div>
                <span className="sr-only" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
                  Step {i + 1} of 3
                </span>
              </>
            )}
          </div>
        </div>
        <div className="gw__body">
          <section className={`gw__scr${i === 0 ? ' on' : ''}`} aria-hidden={i !== 0}>
            <h2 id="gw-h0">{heads[0]}</h2>
            <p>
              EntreDash takes an idea from first spark to a business you can build, one step at a time. You start with the idea itself, and three advisors test it with you from there.
            </p>
          </section>
          <section className={`gw__scr${i === 1 ? ' on' : ''}`} aria-hidden={i !== 1}>
            <h2 id="gw-h1">{heads[1]}</h2>
            <div className="gw__path">
              <ol className="gw__stairs">
                {PATH.map((p) => (
                  <li key={p.t} className="gw__t">
                    <b aria-hidden="true" />
                    <strong>{p.t}</strong>
                    <span>{p.s}</span>
                  </li>
                ))}
              </ol>
            </div>
            <p className="gw__prog">
              <strong>One step at a time.</strong> Each advisor builds on what the last one learned, so you never start over. The rest of EntreDash is there when you&rsquo;re ready for it.
            </p>
          </section>
          <section className={`gw__scr${i === 2 ? ' on' : ''}`} aria-hidden={i !== 2}>
            <h2 id="gw-h2">{heads[2]}</h2>
            <p>
              {hasIdea
                ? 'The Product Strategist picks it up from here and asks whether anyone needs it.'
                : 'Write it down in your own words. The Product Strategist picks it up from there and asks whether anyone needs it.'}
            </p>
            <p className="gw__quote">
              Your idea is stored encrypted. It&rsquo;s never used to train AI, never published, and <span>never seen by a human</span> unless you request a practitioner connection.
            </p>
          </section>
        </div>
        <div className="gw__foot">
          {brief ? (
            <span style={{ marginRight: 'auto' }} />
          ) : (
            <>
              <button type="button" className="g-text" onClick={() => close('skip')}>Skip</button>
              <button type="button" className={`g-btn g-btn--ghost${i === 0 ? ' is-hidden' : ''}`} tabIndex={i === 0 ? -1 : 0} onClick={() => setI(Math.max(0, i - 1))}>
                Back
              </button>
            </>
          )}
          <button type="button" ref={nextRef} className="g-btn g-btn--pri" onClick={next}>
            {brief ? 'Got it' : i === 2 ? 'Show me where' : 'Next'}
          </button>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------- Spotlight

const NOTES: Record<'idea' | 'strategist', { desktop: [string, string]; phone: [string, string] }> = {
  idea: {
    desktop: ['Capture your idea here.', 'Write it the way you\u2019d explain it to a friend. You can refine it later.'],
    phone: ['Tap Capture to start.', 'Write your idea the way you\u2019d explain it to a friend.'],
  },
  strategist: {
    desktop: ['Then meet the Product Strategist.', 'Start a session on your idea. The Product Strategist asks whether there\u2019s a real need and who would pay. You finish with your first memo.'],
    phone: ['Your advisors live here.', 'Open Advisors and start with the Product Strategist.'],
  },
};

function findTarget(step: 'idea' | 'strategist', isPhone: boolean): HTMLElement | null {
  const sel = isPhone
    ? step === 'idea' ? '[data-guide-tab="capture"] .tabbar__cap' : '[data-guide-tab="advisors"]'
    : step === 'idea' ? '[data-guide="capture-btn"]' : '.sidebar [data-guide="market"]';
  const els = Array.from(document.querySelectorAll<HTMLElement>(sel));
  return els.find((el) => {
    const r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0 && r.bottom > 0 && r.top < window.innerHeight;
  }) ?? null;
}

type Hole = { x: number; y: number; w: number; h: number; r: number };

function holePath({ x, y, w, h, r }: Hole) {
  return `M${x + r} ${y}H${x + w - r}A${r} ${r} 0 0 1 ${x + w} ${y + r}V${y + h - r}A${r} ${r} 0 0 1 ${x + w - r} ${y + h}H${x + r}A${r} ${r} 0 0 1 ${x} ${y + h - r}V${y + r}A${r} ${r} 0 0 1 ${x + r} ${y}Z`;
}

function Spotlight({ step, isPhone, stepNo, onClose }: { step: 'idea' | 'strategist'; isPhone: boolean; stepNo: number; onClose: (how: 'got' | 'target' | 'skip' | 'missing') => void }) {
  const [hole, setHole] = useState<Hole | null>(null);
  const [vw, setVw] = useState(window.innerWidth);
  const [vh, setVh] = useState(window.innerHeight);
  const targetRef = useRef<HTMLElement | null>(null);
  const callRef = useRef<HTMLDivElement>(null);
  const [callH, setCallH] = useState(150);
  const closedRef = useRef(false);
  const done = (how: 'got' | 'target' | 'skip' | 'missing') => {
    if (closedRef.current) return;
    closedRef.current = true;
    onClose(how);
  };

  // Find the target: open its rail section, bring it into view, then measure. Give up after 1.5s.
  useEffect(() => {
    let cancelled = false;
    let raf = 0;
    const started = performance.now();
    if (!isPhone && step === 'strategist') {
      const nav = useNavOpen.getState();
      if (!nav.open.includes('advisors')) nav.toggle('advisors');
    }
    let scrolled = false;
    const tick = () => {
      if (cancelled) return;
      // Whatever the target opened (the capture dialog, say) takes over: step aside.
      if (otherDialogOpen()) {
        done('target');
        return;
      }
      const el = findTarget(step, isPhone);
      if (el && !scrolled) {
        scrolled = true;
        el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
      if (el && performance.now() - started > (isPhone ? 60 : 320)) {
        if (targetRef.current !== el) {
          targetRef.current?.classList.remove('is-guided');
          targetRef.current = el;
          if (!isPhone && step === 'strategist') el.classList.add('is-guided');
        }
        const r = el.getBoundingClientRect();
        const pad = isPhone && step === 'idea' ? 6 : 4;
        const next: Hole = isPhone
          ? step === 'idea'
            ? { x: r.left - pad, y: r.top - pad, w: r.width + pad * 2, h: r.height + pad * 2, r: (r.width + pad * 2) / 2 }
            : { x: r.left + 4, y: r.top + 6, w: r.width - 8, h: r.height - 12, r: 12 }
          : { x: r.left - pad, y: r.top - pad, w: r.width + pad * 2, h: r.height + pad * 2, r: 10 };
        setHole((h) => (h && Math.abs(h.x - next.x) < 0.5 && Math.abs(h.y - next.y) < 0.5 && Math.abs(h.w - next.w) < 0.5 ? h : next));
        setVw(window.innerWidth);
        setVh(window.innerHeight);
      } else if (!el && performance.now() - started > 1500) {
        done('missing');
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      targetRef.current?.classList.remove('is-guided');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, isPhone]);

  useEffect(() => {
    if (hole) analytics.track('guide_spotlight_shown', { step, surface: isPhone ? 'phone' : 'desktop' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!hole]);

  // Clicking the real target passes through the hole and ends the spotlight.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const el = targetRef.current;
      if (closedRef.current || !el || !(e.target instanceof Node) || !el.contains(e.target)) return;
      analytics.track('guide_spotlight_target_clicked', { step });
      done('target');
    };
    window.addEventListener('pointerdown', onClick as EventListener, true);
    window.addEventListener('click', onClick, true);
    return () => {
      window.removeEventListener('pointerdown', onClick as EventListener, true);
      window.removeEventListener('click', onClick, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Lock page scroll while pointing; Esc dismisses.
  useEffect(() => {
    const root = document.documentElement;
    const prev = root.style.overflow;
    root.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        analytics.track('guide_spotlight_dismissed', { step, how: 'esc' });
        done('got');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      root.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    if (callRef.current) setCallH(callRef.current.offsetHeight);
  }, [hole, isPhone]);
  useEffect(() => {
    if (hole) callRef.current?.querySelector<HTMLButtonElement>('.g-btn')?.focus({ preventScroll: true });
  }, [!!hole]);

  if (!hole) return null;

  const [title, body] = NOTES[step][isPhone ? 'phone' : 'desktop'];
  const supportsPath = typeof CSS !== 'undefined' && CSS.supports?.('clip-path', 'path(evenodd, "M0 0H1V1H0Z")');
  const scrimStyle: CSSProperties = supportsPath ? { clipPath: `path(evenodd, "M0 0H${vw}V${vh}H0Z ${holePath(hole)}")` } : { background: 'transparent' };

  // Callout placement: beside the target on desktop (or below it when there's no room), above it on phone.
  let callStyle: CSSProperties;
  let callCls = 'gs-call';
  const cx = hole.x + hole.w / 2;
  if (isPhone) {
    callCls += ' gs-call--above';
    callStyle = { bottom: vh - hole.y + 14, ['--nub-x' as string]: `${Math.max(20, Math.min(vw - 32 - 16, cx - 16 - 6))}px` };
  } else if (hole.x + hole.w + 16 + 300 + 16 <= vw) {
    const top = Math.max(16, Math.min(hole.y + hole.h / 2 - 32, vh - callH - 16));
    callStyle = { left: hole.x + hole.w + 16, top, ['--nub-y' as string]: `${Math.max(14, Math.min(callH - 26, hole.y + hole.h / 2 - top - 6))}px` };
  } else {
    callCls += ' gs-call--below';
    const left = Math.max(16, Math.min(cx - 150, vw - 316));
    callStyle = { left, top: hole.y + hole.h + 14, ['--nub-x' as string]: `${Math.max(14, Math.min(274, cx - left - 6))}px` };
  }

  return (
    <>
      <div
        className="gs-scrim"
        style={scrimStyle}
        data-guide-ui
        onClick={() => {
          analytics.track('guide_spotlight_dismissed', { step, how: 'scrim' });
          done('got');
        }}
      />
      <div
        className={`gs-ring${isPhone ? ' gs-ring--dark' : ''}`}
        style={{ left: hole.x, top: hole.y, width: hole.w, height: hole.h, borderRadius: hole.r }}
        aria-hidden="true"
      />
      <div ref={callRef} className={callCls} style={callStyle} role="dialog" aria-modal="false" aria-labelledby="gs-title" data-guide-ui>
        <div className="gs-step">Step {stepNo} of 3</div>
        <h3 id="gs-title">{title}</h3>
        <p>{body}</p>
        <div className="gs-acts">
          <button
            type="button"
            className="g-btn g-btn--pri"
            onClick={() => {
              analytics.track('guide_spotlight_dismissed', { step, how: 'got_it' });
              done('got');
            }}
          >
            Got it
          </button>
          <button type="button" className="g-text" onClick={() => done('skip')}>Skip the guide</button>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------- First memo card

function MemoCard({ ideaTitle, onOpen, onLater }: { ideaTitle: string | null; onOpen: () => void; onLater: () => void }) {
  const openRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    openRef.current?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onLater();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onLater]);
  return (
    <>
      <div className="gs-scrim gs-scrim--plain" data-guide-ui onClick={onLater} />
      <div className="gm" role="dialog" aria-modal="true" aria-labelledby="gm-title" data-guide-ui>
        <div className="gm__seal-row">
          <span className="g-seal" aria-hidden="true" />
          <span className="gm__tag">Memo 01</span>
        </div>
        <h3 id="gm-title">Your first memo is ready</h3>
        <p>
          The memo on {ideaTitle ? <strong>{ideaTitle}</strong> : 'your idea'}. Read the verdict, then decide what to test next.
        </p>
        <div className="gm__acts">
          <button
            type="button"
            ref={openRef}
            className="g-btn g-btn--pri"
            onClick={() => {
              analytics.track('guide_memo_card_opened');
              onOpen();
            }}
          >
            Open memo
          </button>
          <button type="button" className="g-text" onClick={onLater}>Later</button>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------- "How to start"

const ROWS: Array<{ step: GuideStep; t: string; s: string }> = [
  { step: 'idea', t: 'Capture your idea', s: 'A sentence or two is plenty.' },
  { step: 'strategist', t: 'Start a strategy session', s: 'A conversation with the Product Strategist, in plain language.' },
  { step: 'memo', t: 'Read your first memo', s: 'Your verdict and what to do next.' },
];

function HowToStart({ guide, isPhone, onMemoPage }: { guide: GuideState; isPhone: boolean; onMemoPage: boolean }) {
  const [, navigate] = useLocation();
  const { cardOpen, cardAuto, set, act } = useGuide();
  const [leaving, setLeaving] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const hoverRef = useRef(false);
  const { done, current } = progress(guide);
  // Pill shows steps finished ("2 of 3 done"), so it never reads as complete while a step is open.
  // "You're set up" waits until they've left the memo page, so nothing changes while they read.
  const setUp = guide.finished && !onMemoPage;

  // Auto-opened card collapses after 8s unless the pointer or focus is inside.
  useEffect(() => {
    if (!cardOpen || !cardAuto) return;
    const t = window.setTimeout(() => {
      if (!hoverRef.current && !rootRef.current?.contains(document.activeElement)) set({ cardOpen: false, cardAuto: false });
    }, 8000);
    return () => window.clearTimeout(t);
  }, [cardOpen, cardAuto, set]);

  // "You're set up" shows once, then fades away for good.
  useEffect(() => {
    if (!setUp) return;
    analytics.track('guide_complete');
    const t = window.setTimeout(() => {
      if (useGuide.getState().cardOpen) return;
      setLeaving(true);
      window.setTimeout(() => void act('celebrated'), 400);
    }, 8000);
    return () => window.clearTimeout(t);
  }, [setUp, act]);

  // Esc and outside clicks close the card.
  useEffect(() => {
    if (!cardOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && set({ cardOpen: false, cardAuto: false });
    const onDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) set({ cardOpen: false, cardAuto: false });
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown);
    };
  }, [cardOpen, set]);

  const toggle = () => {
    if (!cardOpen) analytics.track('guide_card_opened', { step: current });
    set({ cardOpen: !cardOpen, cardAuto: false });
  };

  const rowDone = (s: GuideStep) => (s === 'idea' ? guide.steps.idea : s === 'strategist' ? guide.steps.strategist : guide.steps.memo === 'opened');

  const goStep = (s: GuideStep) => {
    set({ cardOpen: false, cardAuto: false });
    if (s === 'memo') {
      if (guide.memoHref) navigate(guide.memoHref);
      else if (guide.ideaId) navigate(`/ideas/${guide.ideaId}/needs-discovery`);
      return;
    }
    window.setTimeout(() => set({ spotlight: s }), 120);
  };

  const memoButton = guide.steps.memo === 'ready' ? 'Open memo' : guide.steps.memo === 'writing' ? null : 'Back to your session';

  return (
    <div ref={rootRef} className={`htg${leaving ? ' is-leaving' : ''}`} data-guide-ui onPointerEnter={() => (hoverRef.current = true)} onPointerLeave={() => (hoverRef.current = false)}>
      <div className={`htg__card${cardOpen ? ' open' : ''}`} id="htg-card" role="dialog" aria-labelledby="htg-title" inert={!cardOpen}>
        <div className="htg__hd">
          <h3 id="htg-title">{setUp ? 'You\u2019re set up' : 'How to start'}</h3>
          <button type="button" className="htg__x" aria-label="Minimize" onClick={() => set({ cardOpen: false, cardAuto: false })}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
          </button>
        </div>
        {guide.preview && (
          <p className="htg__preview">Admin preview: you&rsquo;re seeing what a new member sees. Only ideas you add from now on count.</p>
        )}
        {ROWS.map((r) => {
          const isDone = rowDone(r.step);
          const isNow = !isDone && guide.next === r.step;
          const sub = r.step === 'memo' && isNow && guide.steps.memo === 'writing' ? 'We\u2019re writing your memo. We\u2019ll let you know when it\u2019s ready.' : r.s;
          const btn = r.step === 'memo' ? memoButton : 'Show me where';
          return (
            <div key={r.step} className={`htg__it${isDone ? ' done' : ''}`}>
              <div className={`htg__st${isDone ? ' done' : isNow ? ' now' : ''}`} aria-label={isDone ? 'Done' : isNow ? 'Next' : 'To do'} role="img" />
              <div>
                <b>{r.t}</b>
                <span>{sub}</span>
                {isNow && btn && (
                  <button type="button" className="htg__go" onClick={() => goStep(r.step)}>
                    {btn}
                  </button>
                )}
              </div>
            </div>
          );
        })}
        {setUp ? (
          <>
            <p className="htg__done">
              You&rsquo;ve captured an idea, run a strategy session and read your first memo. The rest of EntreDash is in the {isPhone ? 'menu' : 'sidebar'} whenever you need it.
            </p>
            <div className="htg__ft" style={{ textAlign: 'right' }}>
              <button type="button" className="g-btn g-btn--pri" style={{ height: 36, minWidth: 96, fontSize: 14 }} onClick={() => void act('celebrated')}>
                Done
              </button>
            </div>
          </>
        ) : (
          <div className="htg__ft">
            <button
              type="button"
              className="g-text"
              onClick={() => {
                analytics.track('guide_hidden', { from: 'card' });
                void act('hide');
                useToasts.getState().show(HIDDEN_TOAST);
              }}
            >
              Hide this guide
            </button>
          </div>
        )}
      </div>
      <button type="button" className="htg__pill" aria-expanded={cardOpen} aria-controls="htg-card" onClick={toggle}>
        {setUp ? (
          <>
            <span className="g-seal g-seal--sm" aria-hidden="true" />
            You&rsquo;re set up
          </>
        ) : (
          <>
            <span className="htg__meter" aria-hidden="true">
              {[0, 1, 2].map((k) => <i key={k} className={k < done ? 'done' : k === done ? 'now' : ''} />)}
            </span>
            How to start <span className="htg__n">&middot; {done} of 3 done</span>
          </>
        )}
      </button>
    </div>
  );
}
